import { useDimensionHydration } from "../../../store/dimension.hydration";
import { createSimultaneousRequests } from "../../../utils/async.utils";
import { LIST_BUILDER_ERRORS } from "../dimensions.errors";

import { validateItem, type ListBuilderDimensionProps } from ".";

export const useListBuilderDimensionHydration = (
    props: ListBuilderDimensionProps,
    setItems: (s: string[]) => void,
): void => {
    const { id, dependencies, requestParameterMap, configuration } = props;
    const { delimiter, validator } = configuration;

    useDimensionHydration(id, dependencies, async (hydrationPayload) => {
        const payloadValues = {
            valueIdLookup: (() => {
                if (requestParameterMap) {
                    return requestParameterMap.value;
                } else {
                    return id;
                }
            })(),
        };

        if (!hydrationPayload[payloadValues.valueIdLookup]) {
            throw new Error(
                LIST_BUILDER_ERRORS.HYDRATION.VALUE_NOT_AVAILABLE_IN_PAYLOAD,
            );
        }

        let selectedValues: string[] = [];
        try {
            if (delimiter) {
                selectedValues =
                    hydrationPayload[payloadValues.valueIdLookup].split(
                        delimiter,
                    );
            } else {
                selectedValues = JSON.parse(
                    hydrationPayload[payloadValues.valueIdLookup],
                );
            }
        } catch (err) {
            throw new Error(
                LIST_BUILDER_ERRORS.HYDRATION.PAYLOAD_VALUE_FAILED_PARSE,
            );
        }

        if (validator) {
            const { validItems, invalidItems } = (
                await createSimultaneousRequests(
                    selectedValues,
                    async (i: string) => {
                        if (await validateItem(i, validator)) {
                            return [i, true] as [string, boolean];
                        } else {
                            return [i, false] as [string, boolean];
                        }
                    },
                )
            ).reduce(
                (acc, [item, valid]) => {
                    if (valid) {
                        acc.validItems.push(item);
                    } else {
                        acc.invalidItems.push(item);
                    }
                    return acc;
                },
                {
                    validItems: [],
                    invalidItems: [],
                } as {
                    validItems: string[];
                    invalidItems: string[];
                },
            );
            setItems(validItems);

            if (invalidItems.length > 0)
                throw new Error(
                    `Could not validate ${invalidItems.length} items`,
                );
        } else {
            setItems(selectedValues);
        }
    });
};
